import React, { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { ProductCard } from "../components/ProductCard";
import { useSEO } from "../lib/useSEO";
import { BreadcrumbSchema, ProductListSchema } from "../components/SEOSchema";
import { ServiceTerms } from "../components/ServiceTerms";
import { ProductGridSkeleton } from "../components/ProductGridSkeleton";
import catalogNavigation from "../data/catalogNavigation.json";

const CATEGORY_SEO = {
  water: {
    title: "NOCTUA RIVIERA — Livraison d'eau 24h/24 Cannes, Nice, Monaco",
    description: "Eaux plates et pétillantes Cristaline, Perrier et Badoit livrées 24h/24 de Cannes à Monaco. Minimum 30 €, livraison fixe 15 €. Commandez votre eau.",
  },
  restauration: {
    title: "NOCTUA RIVIERA — Pizzas & restauration livrées la nuit Cannes-Monaco",
    description: "Pizzas, pâtes et desserts livrés de 11h à 4h de Cannes à Monaco. Minimum 30 €, livraison 15 € fixes. Espèces, carte bancaire ou lien de paiement.",
  },
  alcohol: {
    title: "NOCTUA RIVIERA — Livraison d'alcool de nuit Cannes, Nice, Monaco",
    description: "Bières, vins de Provence, champagne, vodka, whisky livrés 24/7 sur la Côte d'Azur. Vente strictement réservée aux 18+. Livraison en 30 min.",
  },
  soft_drinks: {
    title: "NOCTUA RIVIERA — Livraison boissons & sodas 24/7 Côte d'Azur",
    description: "Coca-Cola, Red Bull, Perrier, Evian, Schweppes livrés au frais chez vous, 24h/24 et 7j/7 de Cannes à Monaco. Commande rapide par téléphone ou en ligne.",
  },
  snacks: {
    title: "NOCTUA RIVIERA — Livraison snacks & chips de nuit Cannes-Monaco",
    description: "Pringles, Doritos, Haribo, Oreo, Kinder — snacks et biscuits livrés à toute heure sur la Côte d'Azur. Livraison rapide, 24/7.",
  },
  planches: {
    title: "NOCTUA RIVIERA — Plateaux charcuterie & fromages livrés Riviera",
    description: "Planches gourmandes charcuterie et fromages affinés livrées chez vous ou à bord, 24h/24 de Cannes à Monaco. Parfait pour vos soirées et yachts.",
  },
  tobacco: {
    title: "NOCTUA RIVIERA — Livraison tabac & cigarettes de nuit Cannes-Monaco",
    description: "Marlboro, Winston, Camel, Philip Morris livrés à domicile 24/7 sur la Côte d'Azur. Vente strictement 18+. Livraison rapide, 30 min moyenne.",
  },
};

const DEFAULT_SHOP_SEO = {
  title: "NOCTUA RIVIERA — Boutique 24/7 Livraison nuit Cannes-Monaco",
  description: "Épicerie livrée 24h/24 de Cannes à Monaco. Restauration de 11h à 4h. Minimum de commande 30 €, livraison fixe 15 €. Découvrez le catalogue.",
};

// Long-form content per category — targets "thin content" issue + long-tail SEO
const CATEGORY_CONTENT = {
  water: {
    intro: "Besoin d'eau à domicile, à l'hôtel ou pour votre soirée ? Noctua Riviera livre des eaux plates et pétillantes de Cannes à Monaco, 24h/24 et 7j/7. Retrouvez les bouteilles de Cristaline, Perrier et Badoit disponibles dans notre catalogue et composez votre commande avec vos autres produits.",
    highlights: [
      "Cristaline : eau plate en bouteille de 50 cl.",
      "Perrier et Badoit : eaux pétillantes en bouteille de 50 cl.",
      "Livraison à Cannes, Antibes, Nice, Villefranche-sur-Mer et Monaco.",
      "Minimum de 30 € de produits, toutes catégories confondues ; livraison à 15 € fixes.",
    ],
    footer: "Espèces, carte bancaire à la livraison ou lien de paiement sécurisé, y compris à Monaco. Le délai et la disponibilité vous sont confirmés lors de la commande.",
  },
  restauration: {
    intro: "Envie d'une vraie pizza cuite au feu de bois à 2h du matin sur la Croisette, ou d'un tiramisu maison livré dans votre suite d'hôtel à Monaco ? Notre carte restauration est pensée pour ces moments où rien d'autre n'est ouvert. Chaque plat est préparé à la commande par nos partenaires napolitains locaux et livré chaud, en 30 minutes en moyenne, de Cannes à Monaco.",
    highlights: [
      "Pizzas napolitaines cuites au feu de bois — pâte fermentée 48h, mozzarella fior di latte, tomates San Marzano.",
      "Pâtes fraîches artisanales : arrabbiata, carbonara, tagliatelles fruits de mer, linguine.",
      "Desserts italiens signature : tiramisu maison, panna cotta, tarte tatin aux pommes de Provence.",
      "Restauration de 11h à 4h du matin, 7j/7 — pour vos repas et dîners tardifs.",
    ],
    footer: "Livraison assurée sur toute la Riviera : Cannes, Antibes, Juan-les-Pins, Cagnes-sur-Mer, Nice, Villefranche-sur-Mer, Beaulieu-sur-Mer, Èze, Cap-d'Ail et Monaco. Un simple appel au 06 23 06 03 08 suffit — notre équipe francophone et anglophone confirme votre commande et vous annonce le délai précis.",
  },
  alcohol: {
    intro: "Champagne pour une soirée improvisée sur la Promenade des Anglais, bouteille de vodka premium pour un after à Monaco, ou simplement un pack de bières fraîches à 3h du matin pour votre suite d'hôtel : notre sélection alcool couvre toutes les envies de la nuit. Sélection stricte de spiritueux, vins de Provence et champagnes, livrée en 30 minutes.",
    highlights: [
      "Champagnes de maisons prestigieuses — sélection variable selon disponibilité.",
      "Vodkas et whiskys premium : Grey Goose, Belvedere, Hennessy, Macallan, Jack Daniel's.",
      "Vins de Provence rosés, Bordeaux et grands crus adaptés à vos repas ou dégustations.",
      "Bières fraîches en pack : Heineken, Corona, Kronenbourg — livrées au frais.",
    ],
    footer: "Vente strictement réservée aux personnes majeures (18 ans et plus). Une pièce d'identité peut être demandée à la livraison. L'abus d'alcool est dangereux pour la santé, à consommer avec modération. Zone de livraison : Cannes à Monaco, 24 heures sur 24.",
  },
  soft_drinks: {
    intro: "Coca-Cola pour un mojito improvisé, Perrier pour digérer, Red Bull pour tenir jusqu'au lever du soleil : nous livrons toutes vos boissons non alcoolisées 24 heures sur 24 sur la Côte d'Azur, toujours bien fraîches. Idéal pour les soirées entre amis, les receptions à bord ou simplement quand le frigo est vide.",
    highlights: [
      "Sodas classiques : Coca-Cola, Fanta Orange, Sprite, Schweppes Tonic — formats 33 cl et 1,5 L.",
      "Boissons énergisantes Red Bull et Monster Energy — canettes et packs disponibles.",
      "Eaux minérales premium : Perrier pétillante, Evian plate.",
      "Jus de fruits et thés glacés : Fuze Tea, jus 100 % fruits pressés.",
    ],
    footer: "Toutes nos boissons sont conservées dans nos entrepôts réfrigérés et livrées au frais, prêtes à consommer. Livraison 24h/24 de Cannes à Monaco en 30 minutes moyenne. Commandez au 06 23 06 03 08 ou sur WhatsApp.",
  },
  snacks: {
    intro: "Pringles, Haribo, Oreo, Kinder... les indispensables des soirées et des moments de creux à toute heure. Que ce soit pour un apéro improvisé sur la Croisette, un after-party à Monaco ou juste combler une fringale à 4h du matin dans votre chambre d'hôtel, on livre.",
    highlights: [
      "Chips Pringles et Doritos — plusieurs saveurs, formats standard et grands.",
      "Bonbons Haribo : Ourson d'or, Tagada, Dragibus, Schtroumpfs.",
      "Biscuits chocolatés Oreo, Kinder, Bueno, Prince.",
      "Fruits secs, mix apéritif, olives — pour accompagner boissons et alcools.",
    ],
    footer: "Vos snacks préférés livrés 24 heures sur 24 sur toute la Riviera, en 30 minutes moyenne. Notre gamme évolue en permanence — n'hésitez pas à demander un produit précis lors de votre appel.",
  },
  planches: {
    intro: "Rien de tel qu'un plateau de charcuterie fine et fromages affinés pour animer une soirée entre amis, un apéritif improvisé à bord d'un yacht ou un dîner tardif à l'hôtel. Nos planches sont assemblées à la commande par nos partenaires artisans, avec des produits sélectionnés d'exception.",
    highlights: [
      "Charcuterie italienne fine : jambon San Daniele, coppa, bresaola, rosette.",
      "Fromages affinés : Comté 24 mois, Brie truffé, Roquefort AOP, Beaufort d'alpage.",
      "Accompagnements gourmands : figues, raisins, noix, miel, pain aux figues.",
      "Formats 2 à 6 personnes — parfait pour soirées, receptions et dîners de nuit.",
    ],
    footer: "Livraison à domicile, à l'hôtel ou à bord de votre yacht (Port Hercule, Port de Nice, Port Vauban Antibes, Port Pierre Canto Cannes). Pour les commandes yacht ou groupes, appelez-nous à l'avance au 06 23 06 03 08 pour organiser la livraison.",
  },
  tobacco: {
    intro: "Un paquet oublié, une soirée qui s'éternise, ou simplement l'envie d'une clope à 3h du matin : nous livrons les grandes marques de cigarettes à toute heure sur la Côte d'Azur. Vente strictement réservée aux personnes majeures — une pièce d'identité peut être demandée à la livraison.",
    highlights: [
      "Marlboro Red, Marlboro Gold — le classique corsé et sa version équilibrée.",
      "Winston Classic Red, Winston Blue — American blend robuste ou plus doux.",
      "Camel Filters, Camel Blue — mélange oriental traditionnel.",
      "Philip Morris Red, Philip Morris Blue — caractère affirmé ou mélange équilibré.",
    ],
    footer: "Vente interdite aux mineurs. Fumer nuit gravement à la santé. Livraison uniquement sur présentation d'une pièce d'identité pour toute personne dont l'âge peut sembler inférieur à 25 ans. Zone couverte : Cannes à Monaco, 24 heures sur 24.",
  },
};

const Shop = () => {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const subcategory = searchParams.get("sub") || "";
  const [products, setProducts] = useState([]);
  const [loadedFor, setLoadedFor] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const filterKey = `${category || ""}:${subcategory}`;
  const loading = loadedFor !== filterKey;
  // These are the same fixed navigation labels as the API, not product data.
  // Render them immediately so the header does not grow after network requests.
  const categories = catalogNavigation.categories;
  const subcategories = catalogNavigation.subcategories[category] || [];

  const seo = (category && CATEGORY_SEO[category]) || DEFAULT_SHOP_SEO;
  useSEO({
    title: seo.title,
    description: seo.description,
    path: category ? `/shop/${category}` : "/shop",
  });

  useEffect(() => {
    const controller = new AbortController();
    setLoadedFor(null);
    setLoadError(null);
    let url = "/products";
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (subcategory) params.set("subcategory", subcategory);
    if (params.size) url += "?" + params.toString();
    api.get(url, { signal: controller.signal }).then((r) => {
      if (controller.signal.aborted) return;
      setProducts(r.data);
      setLoadedFor(filterKey);
    }).catch(() => {
      if (controller.signal.aborted) return;
      setProducts([]);
      setLoadError("Le catalogue n'a pas pu être chargé. Veuillez réessayer.");
      setLoadedFor(filterKey);
    });
    return () => controller.abort();
  }, [category, subcategory, filterKey, retryCount]);

  const setSub = (slug) => {
    if (slug) setSearchParams({ sub: slug });
    else setSearchParams({});
  };

  const currentCat = categories.find((c) => c.slug === category);

  // Breadcrumb trail
  const breadcrumbTrail = [{ name: "Accueil", path: "/" }, { name: "Boutique", path: "/shop" }];
  if (category && currentCat) {
    breadcrumbTrail.push({ name: currentCat.name, path: `/shop/${category}` });
  }

  return (
    <div data-testid="shop-page" className="min-h-screen">
      <BreadcrumbSchema trail={breadcrumbTrail} />
      {category && currentCat && (
        <ProductListSchema
          categoryName={currentCat.name}
          categoryPath={`/shop/${category}`}
          products={loading ? [] : products}
        />
      )}
      {/* Header */}
      <section className="bg-night-2 border-b border-subtle" data-testid="shop-header">
        <div className="container-x px-5 sm:px-8 py-12 sm:py-16">
          <p className="eyebrow mb-3" data-testid="shop-category-eyebrow">{currentCat?.name_en || "Catalogue"}</p>
          <h1 className="font-display text-5xl sm:text-6xl text-white leading-none" data-testid="shop-title">
            {currentCat?.name || "Tous les produits"}
          </h1>
          {currentCat?.tagline && (
            <p className="text-gray-400 mt-4 max-w-2xl" data-testid="shop-tagline">{currentCat.tagline}</p>
          )}
          <ServiceTerms testId="shop-service-terms" className="mt-5" />
          {category === "alcohol" && <Link to="/livraison-alcool-24h24" className="inline-block mt-4 text-sm text-gold underline underline-offset-4 hover:text-white transition-colors" data-testid="shop-alcohol-day-night-link">Livraison d’alcool 24h/24, aussi en journée : villes et conditions →</Link>}

          <div className="flex flex-wrap gap-2 mt-8">
            <Link
              to="/shop/alcohol"
              className={`text-xs uppercase tracking-wider px-4 py-2 border transition-colors ${
                category === "alcohol" ? "bg-[#d4af37] text-[#0a0f1a] border-[#d4af37]" : "border-white/15 text-gray-300 hover:border-[#d4af37]"
              }`}
              data-testid="filter-alcohol"
            >
              Alcool
            </Link>
            <Link
              to="/shop/soft_drinks"
              className={`text-xs uppercase tracking-wider px-4 py-2 border transition-colors ${
                category === "soft_drinks" ? "bg-[#d4af37] text-[#0a0f1a] border-[#d4af37]" : "border-white/15 text-gray-300 hover:border-[#d4af37]"
              }`}
              data-testid="filter-soft_drinks"
            >
              Soft Drinks
            </Link>
            <Link
              to="/shop/water"
              className={`text-xs uppercase tracking-wider px-4 py-2 border transition-colors ${
                category === "water" ? "bg-[#d4af37] text-[#0a0f1a] border-[#d4af37]" : "border-white/15 text-gray-300 hover:border-[#d4af37]"
              }`}
              data-testid="filter-water"
            >
              Eaux
            </Link>
            <Link
              to="/shop/snacks"
              className={`text-xs uppercase tracking-wider px-4 py-2 border transition-colors ${
                category === "snacks" ? "bg-[#d4af37] text-[#0a0f1a] border-[#d4af37]" : "border-white/15 text-gray-300 hover:border-[#d4af37]"
              }`}
              data-testid="filter-snacks"
            >
              Snacks
            </Link>
            <Link
              to="/shop/planches"
              className={`text-xs uppercase tracking-wider px-4 py-2 border transition-colors ${
                category === "planches" ? "bg-[#d4af37] text-[#0a0f1a] border-[#d4af37]" : "border-white/15 text-gray-300 hover:border-[#d4af37]"
              }`}
              data-testid="filter-planches"
            >
              Planches
            </Link>
            <Link
              to="/shop/restauration"
              className={`text-xs uppercase tracking-wider px-4 py-2 border transition-colors ${
                category === "restauration" ? "bg-[#d4af37] text-[#0a0f1a] border-[#d4af37]" : "border-white/15 text-gray-300 hover:border-[#d4af37]"
              }`}
              data-testid="filter-restauration"
            >
              Restauration
            </Link>
            <Link
              to="/shop/tobacco"
              className={`text-xs uppercase tracking-wider px-4 py-2 border transition-colors ${
                category === "tobacco" ? "bg-[#d4af37] text-[#0a0f1a] border-[#d4af37]" : "border-white/15 text-gray-300 hover:border-[#d4af37]"
              }`}
              data-testid="filter-tobacco"
            >
              Tabac
            </Link>
          </div>

          {subcategories.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t border-white/5" data-testid="subcategory-filters">
              <button
                onClick={() => setSub("")}
                data-testid="sub-filter-all"
                className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
                  !subcategory
                    ? "bg-[#d4af37]/10 text-[#d4af37] border-[#d4af37]"
                    : "border-white/10 text-gray-400 hover:text-[#d4af37]"
                }`}
              >
                Tout · {currentCat?.name || ""}
              </button>
              {subcategories.map((s) => (
                <button
                  key={s.slug}
                  onClick={() => setSub(s.slug)}
                  data-testid={`sub-filter-${s.slug}`}
                  className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border transition-colors ${
                    subcategory === s.slug
                      ? "bg-[#d4af37]/10 text-[#d4af37] border-[#d4af37]"
                      : "border-white/10 text-gray-400 hover:text-[#d4af37]"
                  }`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Products */}
      <section className="container-x px-5 sm:px-8 py-12 sm:py-16" data-testid="shop-products-section" aria-busy={loading}>
        {(loading || products.length > 0) && (
          <p className={`text-xs uppercase tracking-[0.2em] text-gray-500 mb-6${loading ? " invisible" : ""}`} data-testid="shop-count" aria-hidden={loading}>
            {loading ? "\u00a0" : `${products.length} produit${products.length > 1 ? "s" : ""}`}
          </p>
        )}
        {loading ? (
          <ProductGridSkeleton />
        ) : loadError ? (
          <div className="text-center py-20" data-testid="shop-load-error" role="alert">
            <p className="text-gray-300" data-testid="shop-load-error-message">{loadError}</p>
            <button type="button" onClick={() => setRetryCount((n) => n + 1)} className="btn-outline-gold mt-6" data-testid="shop-retry-button">Réessayer</button>
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20" data-testid="shop-empty">
            <p className="font-display text-3xl text-white">Aucun produit pour cette catégorie</p>
            <Link to="/" className="btn-outline-gold mt-6 inline-flex" data-testid="shop-empty-home-link">Retour à l'accueil</Link>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6" data-testid="shop-products-grid">
              {products.map((p, i) => (
                <div key={p.id} className="fade-up" style={{ animationDelay: `${(i % 8) * 50}ms` }}>
                  <ProductCard product={p} />
                </div>
              ))}
            </div>
          </>
        )}
      </section>

      {/* CATEGORY EDITORIAL CONTENT — SEO: fixes "thin content" issue */}
      {category && CATEGORY_CONTENT[category] && (
        <section className="bg-[#0e1524] border-t border-white/8 py-20 sm:py-24" data-testid="category-editorial">
          <div className="container-x max-w-5xl">
            <p className="eyebrow mb-4">À propos de cette catégorie</p>
            <h2 className="font-display text-3xl sm:text-4xl text-white leading-tight mb-8">
              {currentCat?.name || ""} <span className="italic text-[#d4af37]">livrée de nuit</span>
            </h2>
            <p className="text-gray-300 leading-relaxed text-[15px] mb-10 max-w-3xl">
              {CATEGORY_CONTENT[category].intro}
            </p>
            <ul className="space-y-4 mb-10 max-w-3xl">
              {CATEGORY_CONTENT[category].highlights.map((h, i) => (
                <li key={i} className="flex gap-4 text-gray-200 leading-relaxed">
                  <span className="text-[#d4af37] mt-1.5 shrink-0">◆</span>
                  <span>{h}</span>
                </li>
              ))}
            </ul>
            <p className="text-sm text-gray-400 leading-relaxed italic max-w-3xl border-l-2 border-[#d4af37]/50 pl-6">
              {CATEGORY_CONTENT[category].footer}
            </p>
          </div>
        </section>
      )}
    </div>
  );
};

export default Shop;
